import type { App } from 'vue';
import { watch } from 'vue';
import type { Router } from 'vue-router';
import type { Pinia } from 'pinia';
import * as Sentry from '@sentry/vue';
import { useAuthStore } from '@/stores/auth';
import { useBotsStore } from '@/stores/bots';
import { wasReportedApiError } from '@/sentryApi';

/**
 * Sentry environment tag.
 *
 * `import.meta.env.MODE` is `development` both for the local `vite` dev server and for the
 * `build:dev` bundle deployed to app-dev, so the local dev server is tagged `local` to keep
 * developer machines out of the `development` environment's issue stream.
 */
const isLocalhost = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(globalThis.location?.hostname ?? '');
const environment = import.meta.env.DEV || isLocalhost ? 'local' : import.meta.env.MODE;

/**
 * Performance tracing sample rate: every page load / navigation outside production, 10% in
 * production to stay within quota once it's enabled there.
 */
const tracesSampleRate = environment === 'production' ? 0.1 : 1.0;

/**
 * Initialise Sentry error monitoring + performance tracing for the hub.
 *
 * Must be called AFTER `app.config.errorHandler` is assigned: the Vue integration wraps whatever
 * handler exists at init time and calls it after reporting, so a handler assigned later would
 * replace Sentry's and silently stop component errors from being captured.
 *
 * Must be called BEFORE `app.use(router)` so `browserTracingIntegration` can hook the router's
 * initial navigation.
 *
 * A no-op when `VITE_SENTRY_DSN` is unset or empty, which is how Sentry is disabled for a mode.
 * Production is currently disabled this way (`.env.production` overrides the DSN to empty) pending DPO
 * approval; delete that override to enable it there.
 *
 * The release is NOT set here on purpose: `@sentry/vite-plugin` injects it into the bundle at
 * build time as `moin-hub@<version>+<short sha>` (see `vite.config.mts`), so the SDK's release
 * always matches the source maps it uploaded and carries the Hub version.
 *
 * Failed API calls (5xx) are reported separately from the axios interceptors, see
 * `src/sentryApi.ts`.
 */
export function initSentry(app: App, router: Router): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  Sentry.init({
    app,
    dsn,
    environment,
    dataCollection: {
      // To disable sending user data and HTTP bodies, uncomment the lines below. For more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#dataCollection
      // userInfo: false,
      // httpBodies: []
    },
    integrations: [
      Sentry.browserTracingIntegration({ router }),
    ],
    // Tracing
    tracesSampleRate,
    /**
     * Known browser noise, not hub bugs:
     * - ResizeObserver loop warnings (Chrome / Firefox wording), harmless and very frequent.
     * - Stale lazy chunks right after a deploy; the router's onError reloads the page for these.
     */
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
      'Failed to fetch dynamically imported module',
      'Importing a module script failed',
      // Requests the browser aborted (navigation, AbortController) or that never reached the API
      // (offline, VPN drop): nothing in the hub to fix, and the API side has its own monitoring.
      /^Network Error$/,
      /^canceled$/,
      'signal is aborted without reason',
      'AbortError',
    ],
    // A 5xx already reported by the API interceptor (src/sentryApi.ts) that a caller then left
    // unhandled would otherwise arrive a second time as an unhandled rejection.
    beforeSend(event, hint) {
      return wasReportedApiError(hint?.originalException) ? null : event;
    },
    // Attach `sentry-trace` / `baggage` headers only to our own API hosts (their CORS echoes the
    // requested headers back, verified against api-dev.moin.ai and api.moin.ai).
    tracePropagationTargets: ['localhost', /^https:\/\/[a-z0-9-]+\.moin\.ai/],
  });
}

/**
 * Keep the Sentry scope in sync with who is logged in and which bot / channel they are working on,
 * so every event and transaction carries:
 *
 * - `user`        → `{ id, username }` from the raw decoded user (NOT the `fauxRole` overlay)
 * - tags          → `bot.id`, `bot.name`, `channel.id`, `bot.staging` (searchable in Sentry)
 * - context `bot` → the same values grouped for the event detail view
 *
 * Call AFTER `app.use(pinia)`. A no-op when Sentry is not initialised (e.g. no DSN for the mode).
 * The watchers live for the whole app lifetime, which is intended.
 */
export function bindSentryContext(pinia: Pinia): void {
  if (!Sentry.getClient()) return;

  const authStore = useAuthStore(pinia);
  const botsStore = useBotsStore(pinia);

  watch(
    () => authStore.user,
    (user) => {
      if (!user || typeof user !== 'object' || !user.username) {
        Sentry.setUser(null);
        return;
      }
      // The hub's username is the e-mail address; we deliberately capture it under `username` only.
      Sentry.setUser({
        id: user._id ?? user.id ?? undefined,
        username: user.username,
      });
    },
    { immediate: true, deep: false },
  );

  watch(
    () => ({
      botId: botsStore.currentBot?.uniqueBotId as string | undefined,
      botName: botsStore.currentBot?.name as string | undefined,
      channelId: botsStore.currentChannelId ?? undefined,
      staging: botsStore.viewStaging,
    }),
    ({ botId, botName, channelId, staging }) => {
      // `undefined` removes a tag, so leaving a bot clears its tags instead of going stale.
      Sentry.setTags({
        'bot.id': botId,
        'bot.name': botName,
        'channel.id': channelId,
        'bot.staging': botId ? String(staging) : undefined,
      });
      Sentry.setContext('bot', botId ? { id: botId, name: botName, channelId, staging } : null);
    },
    { immediate: true },
  );
}
